import { useLiveQuery } from "dexie-react-hooks";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { shiftMonth } from "@/domain/accounting-period";
import { Decimal } from "@/domain/money";
import { copyBudgetFromPreviousMonth, setCategoryBudget, setIncomeBudget } from "@/data/planning";
import { categoryBreakdown } from "@/finance/budget";
import { computeMonth } from "@/finance/month";
import { formatEurPlain, formatEurSigned, formatMonth, formatMonthTitle, formatPercent } from "@/lib/format";
import { BudgetMeter } from "@/ui/charts";
import { Figure, Notice, Panel, PeriodSwitcher } from "@/ui/primitives";
import { db, monthParam, useFinanceIndex, useToday } from "../hooks";

/** "450.00" → "450,00" for editing; "" when there is no budget line. */
function toInput(amount: string | undefined): string {
  if (amount === undefined) return "";
  return new Decimal(amount).toFixed(2).replace(".", ",");
}

function BudgetInput({ label, initial, onSave }: { label: string; initial: string; onSave: (value: string) => Promise<void> }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "saved">("idle");
  return (
    <span className="flex flex-col items-end">
      <input
        aria-label={label}
        inputMode="decimal"
        value={value}
        placeholder="—"
        onChange={(e) => {
          setValue(e.target.value);
          setState("idle");
        }}
        onBlur={async () => {
          if (value.trim() === initial.trim()) return;
          try {
            await onSave(value);
            setError(null);
            setState("saved");
          } catch (e) {
            setError((e as Error).message);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        className={`amount w-24 rounded-md border px-2 py-1.5 text-right ${error ? "border-over" : state === "saved" ? "border-under" : "border-line"} bg-surface focus:border-petrol focus:outline-none`}
      />
      {error ? <span className="mt-0.5 text-xs text-over">{error}</span> : null}
    </span>
  );
}

export function BudgetPage() {
  const { month: current } = useToday();
  const month = monthParam(useParams().ym, current);
  const index = useFinanceIndex();
  const incomeBudget = useLiveQuery(() => db.incomeBudgets.get(month), [month]);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  if (!index) return <p className="text-muted">Caricamento…</p>;

  const m = computeMonth(index, month);
  const groups = categoryBreakdown(index, [month], { includeEmpty: true });
  const lines = index.budgetsByMonth.get(month);
  const variance = new Decimal(m.budget.variance);

  return (
    <div className="flex flex-col gap-5">
      <PeriodSwitcher
        label={formatMonthTitle(month)}
        previousHref={`/budget/${shiftMonth(month, -1)}`}
        nextHref={`/budget/${shiftMonth(month, 1)}`}
        previousLabel={`Budget di ${formatMonth(shiftMonth(month, -1))}`}
        nextLabel={`Budget di ${formatMonth(shiftMonth(month, 1))}`}
      >
        <p className="text-sm text-muted">Budget e spese effettive</p>
      </PeriodSwitcher>

      <div className="grid grid-cols-2 gap-x-4 gap-y-5 rounded-xl border border-line bg-surface p-4 md:grid-cols-4 md:p-5">
        <Figure label="Budget spese" amount={m.budget.expenses} />
        <Figure label="Speso" amount={m.expenses.total} hint={m.budget.usedRatio ? `${formatPercent(m.budget.usedRatio, 0)} del budget` : undefined} />
        <Figure label={variance.isNegative() ? "Oltre il budget" : "Residuo"} amount={variance.abs().toString()} />
        <Figure label="Risparmio previsto" amount={m.savings.projected} signed />
      </div>

      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      {!lines || lines.size === 0 ? (
        <Notice>
          Nessun budget per {formatMonth(month)}.{" "}
          <button
            type="button"
            className="font-semibold underline underline-offset-4"
            onClick={async () => {
              try {
                const n = await copyBudgetFromPreviousMonth(db, month);
                setMessage({ tone: "success", text: `Copiate ${n} righe di budget da ${formatMonth(shiftMonth(month, -1))}.` });
              } catch (e) {
                setMessage({ tone: "error", text: (e as Error).message });
              }
            }}
          >
            Copia il budget di {formatMonth(shiftMonth(month, -1))}
          </button>{" "}
          oppure inserisci gli importi qui sotto.
        </Notice>
      ) : null}

      <Panel title="Entrate previste">
        <div className="flex flex-wrap items-end gap-6 text-sm">
          <label className="flex items-center gap-3">
            <span>Stipendio</span>
            <BudgetInput
              key={`s-${month}-${incomeBudget?.salary}`}
              label="Stipendio previsto"
              initial={toInput(incomeBudget?.salary)}
              onSave={(v) => setIncomeBudget(db, month, v, incomeBudget?.extra ?? "")}
            />
          </label>
          <label className="flex items-center gap-3">
            <span>Altre entrate</span>
            <BudgetInput
              key={`e-${month}-${incomeBudget?.extra}`}
              label="Altre entrate previste"
              initial={toInput(incomeBudget?.extra)}
              onSave={(v) => setIncomeBudget(db, month, incomeBudget?.salary ?? "", v)}
            />
          </label>
          <span className="text-muted">
            Effettive: <span className="amount">{formatEurPlain(m.income.total)}</span>
          </span>
        </div>
      </Panel>

      {groups.map((g) => (
        <Panel
          key={g.groupId}
          title={g.label}
          action={
            <span className="amount text-sm">
              {formatEurPlain(g.actual)} <span className="text-muted">/ {formatEurPlain(g.budget)}</span>
            </span>
          }
        >
          <BudgetMeter actual={Number(g.actual)} budget={Number(g.budget)} />
          <table className="mt-2 w-full text-sm">
            <thead className="sr-only">
              <tr>
                <th>Categoria</th>
                <th>Budget</th>
                <th>Speso</th>
                <th>Differenza</th>
              </tr>
            </thead>
            <tbody>
              {g.categories.map((c) => {
                const budgetLine = lines?.get(c.categoryId);
                const over = new Decimal(c.variance).isNegative() && new Decimal(c.actual).gt(0);
                return (
                  <tr key={c.categoryId} className="border-t border-line">
                    <td className="py-1.5 pr-2">
                      {c.label}
                      {over ? <span className="block text-xs text-over">Oltre di {formatEurPlain(new Decimal(c.variance).neg())}</span> : null}
                    </td>
                    <td className="py-1.5 text-right">
                      <BudgetInput
                        key={`${month}-${c.categoryId}-${budgetLine?.toString()}`}
                        label={`Budget ${c.label}`}
                        initial={toInput(budgetLine?.toString())}
                        onSave={(v) => setCategoryBudget(db, month, c.categoryId, v)}
                      />
                    </td>
                    <td className="amount w-24 py-1.5 text-right">{formatEurPlain(c.actual)}</td>
                    <td className={`amount hidden w-24 py-1.5 text-right sm:table-cell ${over ? "text-over" : "text-muted"}`}>{formatEurSigned(c.variance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      ))}

      {groups.length === 0 ? (
        <Notice>
          Nessuna categoria: <Link to="/importa" className="font-semibold underline underline-offset-4">importa il file Excel</Link>.
        </Notice>
      ) : null}
    </div>
  );
}
